import React, { useRef, useState } from "react";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType, TableProps } from 'antd';
import { Button, Input, message, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./add_line_invoice_return.css";
import useSWR from 'swr';
import axios from 'axios';
import { debounce } from "lodash";


interface DataType {
    key: string;
    item_code: string;
    item_name: string;
    uom: string;
    qty: number;
    return_qty: number;
    price: number;
    disc_percent: number;
    disc_amount: number;
    status_part: string;
}


type DataIndex = keyof DataType;

const fetcher = (url: string) => axios.get(url).then(res => res.data);
interface AddLineInvoiceReturnProps {
    invoiceNo: string;
    onImport: (selectedItems: DataType[]) => void;
    existingItemCodes: string[];
}

const AddLineInvoiceReturn: React.FC<AddLineInvoiceReturnProps> = ({ invoiceNo, onImport, existingItemCodes }) => {

    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<DataType[]>([]);

    const { data, error } = useSWR<DataType[]>(
        invoiceNo ? `${process.env.REACT_APP_BACKEND_URL}/api/sales-invoice-items/${invoiceNo}` : null,
        fetcher
    );

    const filteredData = data?.filter(item => !(existingItemCodes || []).includes(item.item_code)) || [];

    if (error) {
        console.error("Error fetching sales invoice items:", error);
    }

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex.replace(/_/g, ' ')}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const handleImportClick = debounce(() => {
        if (selectedRows.length === 0) {
            message.warning('Please select at least one item to import.');
            return;
        }
        onImport(selectedRows);
        setSelectedRowKeys([]);
        setSelectedRows([]);
    }, 300);

     const onSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: DataType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };


    const rowSelection: TableProps<DataType>['rowSelection'] = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record: DataType) => ({
            disabled: record.status_part === '99',
            name: record.item_name,
        }),
    };


    const columns: TableColumnsType<DataType> = [
        {
            title: 'Item No',
            dataIndex: 'item_code',
            key: 'item_code',
            ...getColumnSearchProps('item_code'),
            sorter: (a, b) => a.item_code.localeCompare(b.item_code),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
            ...getColumnSearchProps('item_name'),
            sorter: (a, b) => a.item_name.localeCompare(b.item_name),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'UoM',
            dataIndex: 'uom',
            key: 'uom',
            ...getColumnSearchProps('uom'),
            sorter: (a, b) => a.uom.localeCompare(b.uom),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Item Qty',
            dataIndex: 'qty',
            key: 'qty',
            ...getColumnSearchProps('qty'),
            sorter: (a, b) => a.qty - b.qty,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Return Qty',
            dataIndex: 'return_qty',
            key: 'return_qty',
            ...getColumnSearchProps('return_qty'),
            sorter: (a, b) => a.return_qty - b.return_qty,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            ...getColumnSearchProps('price'),
            sorter: (a, b) => a.price - b.price,
            sortDirections: ['descend', 'ascend'],
            render: (text: number) => Number(text).toLocaleString('id-ID'),
        },
        {
            title: 'Disc %',
            dataIndex: 'disc_percent',
            key: 'disc_percent',
            ...getColumnSearchProps('disc_percent'),
            sorter: (a, b) => a.disc_percent - b.disc_percent,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Discount Amount',
            dataIndex: 'disc_amount',
            key: 'disc_amount',
            ...getColumnSearchProps('disc_amount'),
            sorter: (a, b) => a.disc_amount - b.disc_amount,
            sortDirections: ['descend', 'ascend'],
            render: (text: number) => Number(text).toLocaleString('id-ID'),
        },
    ];


    return (
        <div id="ieml" style={transformStyle}>
            <div className="gjs-grid-row" id="idsq">
                <div className="gjs-grid-column" id="ita9">
                    <Table<DataType>
                        rowSelection={rowSelection}
                        columns={columns}
                        dataSource={filteredData}
                        loading={!data && !error && !!invoiceNo}
                    />
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                        <Button
                            type="primary"
                            onClick={handleImportClick}
                            disabled={selectedRowKeys.length === 0}
                        >
                            Import
                        </Button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default AddLineInvoiceReturn;